import React from "react";
import {
  Button,
  Card,
  Stack,
  Shelf,
  Text,
  Position,
  Tooltip,
} from "@chaser/components";
import { Check, Users, X } from "lucide-react";
import RoleGate from "../../../../../components/FeatureGate/RoleGate/index.jsx";
import { toSentenceCaseFeatureLabel } from "../planHelpers";
import styles from "../Addons.module.scss";
import { roles } from "../../../../../util/constants.js";
import useOrganisationInfo from "../../../../../hooks/useOrganisationInfo";
import useRemoteConfig from "../../../../../hooks/useRemoteConfig";
import emitter from "../../../../../util/emitter";
import { getPlanDetectionState } from "./planDetectionUtils";

const getRecurringLabel = (recurringSuffix) => {
  if (String(recurringSuffix || "").includes("/yr")) {
    return "per year (excl VAT)";
  }

  return "per month (excl VAT)";
};

const getFreeUsersLabel = (freeUsers) => {
  const value = Number(freeUsers);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value >= 9999 ? "Unlimited" : String(value);
};

const toFeatureRows = (plan) => {
  const metadataRows = (plan.metadataFeatureFlags || [])
    .map((feature, index) => ({
      id: feature?.id || `metadata-${plan.name}-${index}`,
      label: feature?.label || "",
      enabled: Boolean(feature?.enabled),
    }))
    .sort((a, b) => {
      if (a.enabled !== b.enabled) {
        return a.enabled ? -1 : 1;
      }

      return a.label.localeCompare(b.label);
    });

  if (metadataRows.length) {
    return metadataRows.filter((feature) => Boolean(feature.label));
  }

  const revenueRows = plan.maxRevenueText
    ? [{ id: "max-revenue", label: plan.maxRevenueText, enabled: true }]
    : [];

  const includedRows = (plan.includedDefaults || []).map((feature, index) => ({
    id: feature?.id || `included-${plan.name}-${index}`,
    label:
      typeof feature === "string"
        ? feature
        : feature?.label || feature?.title || "",
    enabled: true,
  }));

  return [...revenueRows, ...includedRows].filter((feature) =>
    Boolean(feature.label),
  );
};

const organisationExceedsMaxRevenueForPlan = (
  calculatedAnnualRevenue,
  plan,
  currencyCode,
) => {
  const maxRevenue = plan.features?.maximumRevenue?.prices?.find(
    (price) => price.currency === currencyCode,
  )?.amount;

  return calculatedAnnualRevenue?.value > maxRevenue;
};

const PlanComparisonGrid = ({
  visiblePlans,
  currentPlanAmount,
  recurringSuffix,
  onSelectPlan,
  actionMode = "compare",
  subscribeCtaLabel = "Start a subscription",
  currencyCode,
}) => {
  const { calculatedAnnualRevenue } = useOrganisationInfo();
  const salesMeetingLink = useRemoteConfig("sales_meeting_link");
  const resolvedCurrentPlanAmount =
    visiblePlans.find((plan) => plan.isCurrent)?.amount ?? currentPlanAmount;

  return (
    <div className={styles.comparePlansGrid}>
      {visiblePlans.map((plan) => {
        const { isCurrentByAmount, isUpgrade, isDowngrade } =
          getPlanDetectionState(plan, resolvedCurrentPlanAmount);
        const freeUsersLabel = getFreeUsersLabel(plan.freeUsers);
        const orgExceedsMaxRevenueForPlan = organisationExceedsMaxRevenueForPlan(
          calculatedAnnualRevenue,
          plan,
          currencyCode,
        );

        const showExceedMaximumModal = () => {
          if (salesMeetingLink) {
            return window.open(salesMeetingLink, "_blank");
          }

          return emitter.emit("openSupportModal", {
            subject: `I am trying to subscribe to ${plan.name} my revenue is ${currencyCode}${calculatedAnnualRevenue.value}`,
            disableSubject: true,
          });
        };

        return (
          <Card
            key={plan.name}
            className={`${styles.comparePlanCard} ${
              plan.isCurrent ? styles.comparePlanCurrent : ""
            } ${
              actionMode === "compare" && isDowngrade
                ? styles.comparePlanDowngrade
                : ""
            }`}
            p="medium"
            style={{
              boxShadow: "none",
              ...(plan.isCurrent
                ? {
                    background: "#fff8f4",
                    border: "2px solid #ee8a67",
                  }
                : {}),
            }}
          >
            <Stack gap="xsmall" justifyContent="space-between" height="100%">
              <Stack dividers>
                <Stack gap="medium" className={styles.comparePlanTopBlock}>
                  <Shelf alignItems="center" justifyContent="space-between">
                    <Text fontWeight="bold" fontSize="heading.2">
                      {plan.title}
                    </Text>
                    {plan.isCurrent && actionMode === "compare" && (
                      <span className={styles.comparePlanCurrentBadge}>
                        CURRENT
                      </span>
                    )}
                  </Shelf>

                  <Stack gap="xsmall">
                    <Text fontSize="heading.2" fontWeight="bold">
                      {plan.formattedAmount}
                    </Text>
                    <Text color="Mid Neutral 1">
                      {getRecurringLabel(recurringSuffix)}
                    </Text>
                  </Stack>
                </Stack>
                <Stack
                  gap="xsmall"
                  py="medium"
                  className={styles.comparePlanIncludedList}
                >
                  {freeUsersLabel && (
                    <Shelf
                      className={styles.comparePlanFreeUsersRow}
                      gap="xsmall"
                      alignItems="center"
                    >
                      <Users size={14} color="#E87750" />
                      <Text className={styles.comparePlanFreeUsersValue}>
                        {freeUsersLabel === "Unlimited"
                          ? "Unlimited free users"
                          : `${freeUsersLabel} free users`}
                      </Text>
                    </Shelf>
                  )}
                  {toFeatureRows(plan).map((feature) => (
                    <Shelf key={feature.id} gap="xsmall" alignItems="center">
                      {feature.enabled ? (
                        <Check size={14} color="#E87750" />
                      ) : (
                        <X size={14} color="#7C8A9C" />
                      )}
                      <Text color={feature.enabled ? "" : "Mid Neutral 1"}>
                        {toSentenceCaseFeatureLabel(feature.label)}
                      </Text>
                    </Shelf>
                  ))}
                </Stack>
              </Stack>

              <RoleGate
                roles={[roles.billing, roles.owner]}
                renderGate={(_, children) => {
                  return (
                    <Tooltip
                      position={Position.BOTTOM}
                      content="This action needs additional permissions."
                    >
                      {React.cloneElement(children, { disabled: true })}
                    </Tooltip>
                  );
                }}
              >
                {(plan.isCurrent || isCurrentByAmount) &&
                actionMode === "compare" ? (
                  <Button appearance="secondary" size="narrow" disabled>
                    Current plan
                  </Button>
                ) : orgExceedsMaxRevenueForPlan ? (
                  <Button
                    appearance="primary"
                    className={styles.comparePlanCta}
                    onClick={showExceedMaximumModal}
                  >
                    Contact us
                  </Button>
                ) : actionMode === "compare" ? (
                  isDowngrade ? (
                    <Button
                      size="narrow"
                      className={styles.comparePlanCta}
                      onClick={() =>
                        onSelectPlan(plan.name, { isDowngrade: true, plan })
                      }
                    >
                      Downgrade
                    </Button>
                  ) : (
                    <Button
                      size="narrow"
                      className={styles.comparePlanCta}
                      onClick={() => onSelectPlan(plan.name)}
                    >
                      {isUpgrade ? `Upgrade to ${plan.title}` : "Switch plan"}
                    </Button>
                  )
                ) : (
                  <Button
                    appearance="primary"
                    className={styles.comparePlanCta}
                    onClick={() => onSelectPlan(plan.name)}
                  >
                    {subscribeCtaLabel}
                  </Button>
                )}
              </RoleGate>
            </Stack>
          </Card>
        );
      })}
    </div>
  );
};

export default PlanComparisonGrid;
